// TESSERAX — NOMS D'EFFACEMENT (cœur pur, testé).
//
// Un effacement d'UNE seule ligne est la respiration de base du jeu ; mais effacer
// PLUSIEURS lignes d'un seul coup « à plat » — hors cascade (qui a son bandeau COMBO)
// et hors étage plein multiple (bandeau GRILLE) — était un FEAT que RIEN ne célébrait :
// le branchement de résolution ne montrait aucun bandeau pour un double / triple /
// quadruple posé d'un coup, ni même pour un PREMIER étage plein (49 cases). On comble
// ce vide de retour avec une échelle de noms ESCALADANTE : la mémoire du joueur retient
// un NOM, pas un nombre de lignes (cf. le « Tetris » du genre).
//
// Vocabulaire CRISTALLIN (cf. DA « Sacred Sci-Fi Geometry ») et VOLONTAIREMENT distinct
// des paliers nommés du pouvoir Zone (`core/zone.ZONE_TIERS` : TÉTRADE…OMNICUBE) pour ne
// pas brouiller le lexique : un gros effacement « à plat » culmine sur une « FAILLE » —
// la fracture maximale du cristal —, l'instant le plus marquant et le plus partageable.
// Le palier `tier` pilote l'intensité du juice (flash/shake/son) côté main.ts sans
// dupliquer la table de seuils.
//
// Module PUR : ni THREE ni DOM, pas de Math.random, pas d'I/O. main.ts mappe la clé
// i18n en libellé (`t(key, …, fallbackFr)`) et applique le juice selon `tier`. C'est de
// l'AFFICHAGE seul → aucun RNG touché, déterminisme du défi du jour intact.

export interface ClearName {
  key: string;        // clé i18n (FR/EN dans les dictionnaires)
  fallbackFr: string; // libellé FR de repli → le module reste autonome / affichable sans i18n
  tier: number;       // 1..4 — intensité croissante (pilote flash/shake/son côté main.ts)
}

// Nom d'un effacement de `rows` lignes effacées d'un seul coup « à plat » (sans cascade
// ni étage plein, qui ont déjà leur bandeau). Renvoie null s'il n'y a rien à célébrer
// (0 ou 1 ligne = respiration de base → pas de bandeau, on garde l'écran calme).
// Tolérant aux entrées non finies / négatives (→ null). N'altère aucun état.
export function lineClearName(rows: number): ClearName | null {
  const n = Number.isFinite(rows) ? Math.floor(rows) : 0;
  if (n < 2) return null;
  if (n === 2) return { key: 'clear.double',    fallbackFr: 'DOUBLE',    tier: 1 };
  if (n === 3) return { key: 'clear.triple',    fallbackFr: 'TRIPLE',    tier: 2 };
  if (n === 4) return { key: 'clear.quadruple', fallbackFr: 'QUADRUPLE', tier: 3 };
  return { key: 'clear.faille', fallbackFr: 'FAILLE', tier: 4 }; // 5+ lignes : la fracture maximale
}

// Nom d'un effacement d'ÉTAGE(S) plein(s) (slab = 49 cases). Un seul étage plein était
// muet côté bandeau (seulement un son) alors que c'est un feat marquant ; on le nomme
// « GRILLE », et au-delà « {n}× GRILLE » (libellé conservé à l'identique de main.ts).
// `tier` grimpe avec le nombre d'étages. Renvoie null si aucun étage plein. Tolérant
// aux entrées non finies / négatives.
export function slabClearName(slabs: number): ClearName | null {
  const n = Number.isFinite(slabs) ? Math.floor(slabs) : 0;
  if (n < 1) return null;
  if (n === 1) return { key: 'clear.grille', fallbackFr: 'GRILLE', tier: 2 };
  return { key: 'clear.grilleN', fallbackFr: '{n}× GRILLE', tier: Math.min(4, 2 + n) };
}

// Nom d'un EFFACEMENT PAR VRILLE (« spin clear ») — la pièce n'a trouvé sa place finale
// QUE par une rotation avec recalage (wall-kick), un tuck qu'aucune chute simple n'atteint,
// et cet ancrage par torsion a effacé `rows` ligne(s). C'est le feat de TRAJECTOIRE par
// excellence du stacker (le « T-spin » du genre), qui ne célébrait jusque-là RIEN ; on lui
// donne son NOM, car la mémoire du joueur retient un nom, pas une manœuvre. Vocabulaire de
// TORSION cristalline, VOLONTAIREMENT distinct des lexiques clears « à plat » (DOUBLE…FAILLE),
// Zone (TÉTRADE…OMNICUBE) et dégagement (SURSIS…). Renvoie TOUJOURS un nom (une vrille
// effaçante a au moins 1 ligne) ; tolérant aux entrées non finies / ≤1 → VRILLE.
export function spinClearName(rows: number): ClearName {
  const n = Number.isFinite(rows) ? Math.floor(rows) : 1;
  if (n <= 1) return { key: 'clear.vrille',  fallbackFr: 'VRILLE',  tier: 1 };
  if (n === 2) return { key: 'clear.torsion', fallbackFr: 'TORSION', tier: 2 };
  return { key: 'clear.spirale', fallbackFr: 'SPIRALE', tier: 3 }; // 3+ lignes vrillées : la torsion maximale
}

// Nom de l'effacement 4D du Pli — quand la COMBINAISON des deux CHAMBRES (Endroit + Envers) complète
// `rows` ligne(s) qu'aucune chambre ne complétait seule : un effacement impossible dans une chambre
// unique, l'apogée au-dessus de la Croix. La mémoire du joueur retient un NOM ; on le baptise du nom
// du jeu lui-même — « TESSERAX » (comme une « Tetris » dans Tetris) : le feat-signature 4D porte la
// marque. Le lexique du pouvoir Zone a été déplacé en conséquence (TÉTRADE…OMNICUBE, cf. core/zone)
// pour que « TESSERAX » appartienne au seul Pli. On garde « tessera » comme nom de CODE (clé i18n,
// hooks de reliques). Renvoie null si le Pli n'efface rien (un repli stérile ne se célèbre pas).
export function tesseraName(rows: number): ClearName | null {
  const n = Number.isFinite(rows) ? Math.floor(rows) : 0;
  if (n < 1) return null;
  if (n === 1) return { key: 'clear.tessera', fallbackFr: 'TESSERAX', tier: 2 };
  return { key: 'clear.tesseraN', fallbackFr: '{n}× TESSERAX', tier: Math.min(4, 2 + n) }; // repli multi-lignes : l'alignement supérieur
}

// Nom d'une CASCADE (réaction en chaîne : un clear fait retomber la pile qui en complète un
// autre, etc.). C'est le feat le plus BEAU et le plus PRÉPARABLE du puits — surtout déclenché
// d'une seule Bascule sur une pile montée pour ça —, mais il ne portait qu'un générique
// « COMBO ×N ». On lui donne enfin son apogée NOMMÉE, pendant des autres lexiques. Le palier
// monte avec la profondeur de chaîne. Renvoie null sous 3 maillons : une cascade de 2 reste la
// respiration « COMBO » (on garde l'écran calme), une cascade ≥3 est l'évènement à célébrer.
// Vocabulaire d'ÉNERGIE en chaîne, VOLONTAIREMENT distinct des lexiques clears « à plat »
// (DOUBLE…FAILLE), vrille (VRILLE…SPIRALE), Zone (TÉTRADE…OMNICUBE) et dégagement (SURSIS…).
// Culmine sur SINGULARITÉ (cf. DA « civilisation de dimension supérieure »). Tolérant aux
// entrées non finies / négatives (→ null). N'altère aucun état.
export function cascadeName(chain: number): ClearName | null {
  const n = Number.isFinite(chain) ? Math.floor(chain) : 0;
  if (n < 3) return null;
  if (n === 3) return { key: 'clear.cascade',     fallbackFr: 'CASCADE',     tier: 1 };
  if (n === 4) return { key: 'clear.deferlement', fallbackFr: 'DÉFERLEMENT', tier: 2 };
  return { key: 'clear.singularite', fallbackFr: 'SINGULARITÉ', tier: 3 }; // 5+ maillons : l'apogée
}
